import { DateTime } from "luxon";
import { IModel, Model, ModelRecord } from "@common/models/Model";
import { StoreModel } from "@common/models/StoreModel";
import { DateTimeUtils } from "@common/utils";
import { ClinicVeterinarianModel } from "@common/models/clinics/ClinicVeterinarianModel";
import { ClinicUserModel, IClinicUserModel } from "@common/models/clinics/ClinicUserModel";
import { IVisitListing, VisitListingModel } from "@common/models/clinics/VisitListingModel";
import { ReservationModel } from "@common/models";

/**
 * FROM: ClinicModel.cs
 */
export interface IClinicModel extends IModel {
	clinicId: number;
	storeId: number;
	scheduledStartDate: string;
	scheduledEndDate: string;
	startDate?: string | undefined;
	endDate?: string | undefined;
	canOpen: boolean;
	canJoin: boolean;
	isPendingClose: boolean;
	allowChangingSingleVisitVet: boolean;
	veterinarianOverrideName: string;
	veterinarianOverrideFirstName: string;
	veterinarianOverrideLastName: string;
	veterinarianOverrideLicense: string;
	veterinarianOverrideEmail: string;
	veterinarianOverrideExpirationDate?: string | undefined;
	veterinarianName: string;
	veterinarianFirstName: string;
	veterinarianLastName: string;
	veterinarianLicense: string;
	veterinarianLicenseExpirationDate?: string | undefined;
	veterinarianEmail: string;
	defaultRabiesSerialNumber: string;
	store: StoreModel;
	veterinarians: ClinicVeterinarianModel[];
	users: IClinicUserModel[];
	visits: IVisitListing[]; // VisitListingModel[];
	reservations: ReservationModel[];
	mobileRig: any; // MobileRigModel;
	serverRootUrl: string;
	isCancelled: boolean;
	isPublished: boolean;
	targetAOV?: number | undefined;
	clinicAOV?: number | undefined;
	wasAOVTargetMet?: boolean | undefined;
	veterinarianSignaturePhotoId?: number | undefined;
	saveVetSignatureToClinic: boolean;
	veterinarianSignaturePhoto: any; // PhotoModel;
	slotDuration: number;
	appointmentCapacity: number;
	cancelledDate?: string | undefined;
	clinicCancellationReasonId?: number | undefined;
	clinicCancelledBy: string;
	clinicCancelledApprovedBy: string;
	clinicCancellationNotes: string;
	clinicCancellationProcessedDate?: string | undefined;
	businessLineId: number;
	dispatchPcc?: number | undefined;
}

export class ClinicModel extends Model implements ModelRecord<IClinicModel> {
	clinicId: number;
	storeId: number;
	scheduledStartDate: DateTime;
	scheduledEndDate: DateTime;
	startTime: string;
	endTime: string;
	startDate: DateTime;
	endDate: DateTime | undefined;
	canJoin: boolean;
	canOpen: boolean;
	isPendingClose: boolean;
	veterinarianName: string;
	veterinarianFirstName: string;
	veterinarianLastName: string;
	veterinarianLicense: string;
	veterinarianLicenseExpirationDate: DateTime | undefined;
	veterinarianEmail: string;
	store: StoreModel | undefined;
	storeName: string;
	storeNumber: string;
	storeAddress: string;
	storeCity: string;
	stateAbbr: string;
	clinicAddress: string;
	isCancelled: boolean;
	veterinarians: ClinicVeterinarianModel[] | undefined;
	users: ClinicUserModel[] | undefined;
	serverRootUrl: string | undefined;
	visits: VisitListingModel[];
	reservations: any[];
	isPublished: boolean;
	saveVetSignatureToClinic: boolean;
	slotDuration: number;
	appointmentCapacity: number;
	cancelledDate: string | undefined;
	clinicCancellationReasonId: number | undefined;
	clinicCancelledBy: string | undefined;
	clinicCancelledApprovedBy: string | undefined;
	clinicCancellationNotes: string | undefined;
	clinicCancellationProcessedDate: string | undefined;
	businessLineId: number;

	allowChangingSingleVisitVet: boolean;
	veterinarianOverrideName: string | undefined;
	veterinarianOverrideFirstName: string | undefined;
	veterinarianOverrideLastName: string | undefined;
	veterinarianOverrideLicense: string | undefined;
	veterinarianOverrideEmail: string | undefined;
	veterinarianOverrideExpirationDate: string | undefined;
	defaultRabiesSerialNumber: string | undefined;

	constructor(params: Partial<IClinicModel>) {
		super(params);
		this.clinicId = _.defaultTo(params.clinicId, 0);
		this.storeId = _.defaultTo(params.storeId, 0);
		this.scheduledStartDate = DateTime.fromISO(_.defaultTo(params.scheduledStartDate, "2000-01-01"), { zone: "utc" });
		this.scheduledEndDate = DateTime.fromISO(_.defaultTo(params.scheduledEndDate, "2000-01-01"), { zone: "utc" });
		this.startTime = DateTimeUtils.getTimehour12(this.scheduledStartDate);
		this.endTime = DateTimeUtils.getTimehour12(this.scheduledEndDate);
		this.startDate = DateTime.fromISO(_.defaultTo(params.startDate, "2000-01-01"), { zone: "utc" });
		this.endDate = params.endDate ? DateTime.fromISO(params.endDate, { zone: "utc" }) : undefined;
		this.canJoin = _.defaultTo(params.canJoin, false);
		this.isPendingClose = _.defaultTo(params.isPendingClose, false);
		this.canOpen = _.defaultTo(params.canOpen, false);
		this.veterinarianName = _.defaultTo(params.veterinarianName, "");
		this.veterinarianFirstName = _.defaultTo(params.veterinarianFirstName, "");
		this.veterinarianLastName = _.defaultTo(params.veterinarianLastName, "");
		this.veterinarianLicense = _.defaultTo(params.veterinarianLicense, "");
		this.veterinarianEmail = _.defaultTo(params.veterinarianEmail, "");
		this.veterinarianLicenseExpirationDate = params.veterinarianLicenseExpirationDate
			? DateTime.fromISO(params.veterinarianLicenseExpirationDate, { zone: "utc" })
			: undefined;
		this.store = params.store ? new StoreModel(params.store) : undefined;
		this.storeName = _.defaultTo(this.store?.name?.split(" ")[0], "");
		this.storeNumber = _.defaultTo(this.store?.name?.split(" ")[1], "");
		this.storeAddress = _.defaultTo(this.store?.address, "");
		this.storeCity = _.defaultTo(this.store?.city, "");
		this.stateAbbr = _.defaultTo(this.store?.state?.abbreviation, "");
		this.clinicAddress = _.defaultTo(this.store?.address + ", " + this.storeCity + ", " + this.stateAbbr + " " + this.store?.zipCode, "");
		this.isCancelled = _.defaultTo(params.isCancelled, false);
		this.veterinarians = params?.veterinarians
			? params?.veterinarians.map(p => new ClinicVeterinarianModel(p))
			: ([] as ClinicVeterinarianModel[]);
		this.users = params?.users ? params?.users.map(p => new ClinicUserModel(p)) : ([] as ClinicUserModel[]);
		this.serverRootUrl = _.defaultTo(params.serverRootUrl, "");

		this.visits = params?.visits ? params?.visits.map(v => new VisitListingModel(v)) : ([] as VisitListingModel[]);
		this.reservations = _.defaultTo(params.reservations, []);
		this.isPublished = _.defaultTo(params.isPublished, false);
		this.saveVetSignatureToClinic = _.defaultTo(params.saveVetSignatureToClinic, false);
		this.slotDuration = _.defaultTo(params.slotDuration, 0);
		this.appointmentCapacity = _.defaultTo(params.appointmentCapacity, 0);
		this.cancelledDate = _.defaultTo(params.cancelledDate, undefined);
		this.clinicCancellationReasonId = _.defaultTo(params.clinicCancellationReasonId, undefined);
		this.clinicCancelledBy = _.defaultTo(params.clinicCancelledBy, undefined);
		this.clinicCancelledApprovedBy = _.defaultTo(params.clinicCancelledApprovedBy, undefined);
		this.clinicCancellationNotes = _.defaultTo(params.clinicCancellationNotes, undefined);
		this.clinicCancellationProcessedDate = _.defaultTo(params.clinicCancellationProcessedDate, undefined);
		this.businessLineId = _.defaultTo(params.businessLineId, 0);

		this.allowChangingSingleVisitVet = _.defaultTo(params.allowChangingSingleVisitVet, false);
		this.veterinarianOverrideName = _.defaultTo(params.veterinarianOverrideName, undefined);
		this.veterinarianOverrideFirstName = _.defaultTo(params.veterinarianOverrideFirstName, undefined);
		this.veterinarianOverrideLastName = _.defaultTo(params.veterinarianOverrideLastName, undefined);
		this.veterinarianOverrideLicense = _.defaultTo(params.veterinarianOverrideLicense, undefined);
		this.veterinarianOverrideEmail = _.defaultTo(params.veterinarianOverrideEmail, undefined);
		this.veterinarianOverrideExpirationDate = _.defaultTo(params.veterinarianOverrideExpirationDate, undefined);
		this.defaultRabiesSerialNumber = _.defaultTo(params.defaultRabiesSerialNumber, undefined);
	}
	mobileRig: any;
	targetAOV: any;
	clinicAOV: any;
	wasAOVTargetMet: any;
	veterinarianSignaturePhotoId: any;
	veterinarianSignaturePhoto: any;
	dispatchPcc: any;
}
